import { getSharedConfig } from "../../createSharedConfig"
import { getBuyRequestMeta, getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import { RackOptions, SlingerXSideConfig } from "@/js/pages/product/rackbuilder/RackTypes"
import { findBundleProduct } from "@/js/pages/product/rackbuilder/utils/product-lookup"
import { getBackSelection, getFrontSelection, getJCupSelection, getDepthSelection, getPlateStorageSelection, getBandPegSelection, getCommonCaveSelections } from "@/js/pages/product/rackbuilder/utils/selection-utils"
import { IRogue, BuyRequest } from "@/js/services/RogueTypes"

export function createSlingerXConfig(rogue: IRogue, buyRequest: BuyRequest): RackOptions {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const rack = findBundleProduct(rogue, buyRequest, 'rack') || getSimpleForBuyRequest(buyRequest, rogue)
  const functionalTrainer = findBundleProduct(rogue, buyRequest, 'functional')

  const sharedConfig = getSharedConfig(rogue, buyRequest)
  const isHalfRack = rack?.attributes?.rack_size === '4'

  const line = rack?.name?.includes('Monster Lite') ? 'RML' : 'RM'
  const numbered = rack?.attributes?.edition === 'Numbered' || rack?.attributes?.upright_type === 'Numbered' || line === 'RM'

  // buyRequest.children[0] is the rack bundle (unchanged); children[1]/[2] are the new SLINGER-X-BUNDLE-LEFT/-RIGHT bundles carrying the per-side attachments Magento split out of the old flat bundle.
  const leftAttachmentsBR = buyRequest.children?.[1]
  const rightAttachmentsBR = buyRequest.children?.[2]

  const weightStackAmount = String(functionalTrainer?.attributes?.edition).includes('300') ? '300' : '250'
  const ftShrouds = Boolean(functionalTrainer && functionalTrainer?.attributes?.rig_or_wm !== 'No')

  const leftSeatSelection = leftAttachmentsBR ? findBundleProduct(rogue, leftAttachmentsBR, 'lat pulldown seat', true)?.name : undefined
  const rightSeatSelection = rightAttachmentsBR ? findBundleProduct(rogue, rightAttachmentsBR, 'lat pulldown seat', true)?.name : undefined

  // Both RM and RML have 2 seat products - with and without the low row kit; the kit mesh only gets inserted for the "+ Low Row Kit" one.
  const leftSeatLowRowKit = !!leftSeatSelection?.toLowerCase().includes('low row kit')
  const rightSeatLowRowKit = !!rightSeatSelection?.toLowerCase().includes('low row kit')

  const leftSwivelPulley = !!(leftAttachmentsBR && findBundleProduct(rogue, leftAttachmentsBR, 'swivel', true))
  const rightSwivelPulley = !!(rightAttachmentsBR && findBundleProduct(rogue, rightAttachmentsBR, 'swivel', true))

  const { lowRowSelection: leftLowRowSelection, latBarSelection: leftLatBarSelection } = leftAttachmentsBR
    ? getCommonCaveSelections(rogue, leftAttachmentsBR, true, sharedConfig.materials || {})
    : { lowRowSelection: undefined, latBarSelection: undefined }

  const { lowRowSelection: rightLowRowSelection, latBarSelection: rightLatBarSelection } = rightAttachmentsBR
    ? getCommonCaveSelections(rogue, rightAttachmentsBR, false, sharedConfig.materials || {})
    : { lowRowSelection: undefined, latBarSelection: undefined }

  const leftSide: SlingerXSideConfig = {
    seatSelection   : leftSeatSelection,
    seatLowRowKit   : leftSeatLowRowKit,
    swivelPulley    : leftSwivelPulley,
    latBarSelection : leftLatBarSelection,
    lowRowSelection : leftLowRowSelection,
  }

  const rightSide: SlingerXSideConfig = {
    seatSelection   : rightSeatSelection,
    seatLowRowKit   : rightSeatLowRowKit,
    swivelPulley    : rightSwivelPulley,
    latBarSelection : rightLatBarSelection,
    lowRowSelection : rightLowRowSelection,
  }

  return {
    ...sharedConfig,
    line,
    numbered,
    height              : sharedConfig.height || '90',
    isFunctionalTrainer : true,
    slingerX            : true,
    weightStackAmount,
    ftShrouds,
    back                : getBackSelection(rogue, buyRequest, meta) || 'nameplate',
    front               : getFrontSelection(rogue, buyRequest, meta),
    jcup                : getJCupSelection(rogue, buyRequest),
    depth               : getDepthSelection(rack, meta),
    sixPost             : !isHalfRack,
    halfRack            : isHalfRack || undefined,
    plateStorage        : getPlateStorageSelection(rogue, buyRequest),
    leftSide            : (leftAttachmentsBR?.qty ?? 0) > 0,
    rightSide           : (rightAttachmentsBR?.qty ?? 0) > 0,
    seatSelection       : leftSeatSelection || rightSeatSelection,
    seatLowRowKit       : leftSeatLowRowKit || rightSeatLowRowKit,
    swivelPulley        : leftSwivelPulley || rightSwivelPulley,
    slingerXSides       : { left: leftSide, right: rightSide },
    bandPegs            : getBandPegSelection(rogue, buyRequest),
  }
}
